import { ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import SEOHead from "@/components/SEOHead";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="Terms of Service | MySabbat"
        description="Read the Terms of Service for using the MySabbat app."
        path="/terms"
      />
      <Header />

      <main className="flex-1 py-12 md:py-20">
        <div className="container">
          <div className="max-w-2xl mx-auto mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to home
            </Link>
          </div>

          <article className="max-w-2xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Terms of Service – MySabbat
            </h1>
            <p className="text-muted-foreground mb-10">Last updated: March 2026</p>

            <div className="bg-card rounded-2xl p-8 md:p-10 shadow-sm border border-border/40 space-y-8">
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">Introduction</h2>
                <p className="text-foreground/80 leading-relaxed">
                  Welcome to MySabbat. These Terms of Service govern your use of the MySabbat mobile application and related services. By using the app, you agree to these terms.
                </p>
                <p className="text-foreground/80 leading-relaxed mt-3">
                  MySabbat is designed to help users plan and structure their gap year by providing tools for planning, goal setting, exploration of opportunities, and guidance.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">Eligibility</h2>
                <p className="text-foreground/80 leading-relaxed">
                  You must be at least 13 years old to use MySabbat. If you are under 18, you confirm that you have permission from a parent or legal guardian.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">User Accounts</h2>
                <p className="text-foreground/80 leading-relaxed mb-3">
                  To use certain features, you may need to create an account. You agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-foreground/80">
                  <li>Provide accurate information</li>
                  <li>Keep your login details secure</li>
                  <li>Be responsible for activity under your account</li>
                </ul>
                <p className="text-foreground/80 leading-relaxed mt-3">
                  MySabbat reserves the right to suspend or terminate accounts that violate these terms.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">Use of the Service</h2>
                <p className="text-foreground/80 leading-relaxed mb-3">
                  You agree to use MySabbat only for lawful purposes. You must not:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-foreground/80">
                  <li>Misuse the app or attempt to disrupt its functionality</li>
                  <li>Attempt to access systems or data you are not authorized to access</li>
                  <li>Upload harmful or illegal content</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">AI Features</h2>
                <p className="text-foreground/80 leading-relaxed">
                  MySabbat may include AI-based guidance tools. These tools are provided for informational purposes only and should not be considered professional, financial, or legal advice.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">Subscriptions and Purchases</h2>
                <p className="text-foreground/80 leading-relaxed">
                  Some features may require a paid subscription (MySabbat Pro). Payments are handled through Apple App Store or other app marketplaces. Subscription terms and billing are governed by those platforms.
                </p>
                <p className="text-foreground/80 leading-relaxed mt-3">
                  Subscriptions automatically renew unless canceled before the renewal date.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">Third-Party Services</h2>
                <p className="text-foreground/80 leading-relaxed">
                  MySabbat may link to third-party services such as job platforms, travel providers, or educational partners. We are not responsible for the content or services provided by those third parties.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">Limitation of Liability</h2>
                <p className="text-foreground/80 leading-relaxed">
                  MySabbat is provided "as is". We do not guarantee uninterrupted or error-free operation. To the fullest extent permitted by law, MySabbat is not liable for indirect or consequential damages.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">Changes to the Terms</h2>
                <p className="text-foreground/80 leading-relaxed">
                  We may update these Terms from time to time. Continued use of the app after updates means you accept the new Terms.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">Contact</h2>
                <p className="text-foreground/80 leading-relaxed">
                  If you have questions about these Terms, you can contact us at:{" "}
                  <a href="mailto:kontakt@mysabbat.dk" className="text-primary hover:underline">
                    kontakt@mysabbat.dk
                  </a>
                </p>
              </section>
            </div>
          </article>
        </div>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default Terms;
